import React, { useCallback, useMemo } from 'react';

import { SearchableFields } from '~/api/dataFields';
import { Link } from '~/eds/src/components/Link/Link';
import { parseDate } from '~/eds/src/utils/parseDate';
import { FieldId } from '~/evifields/src/types/FieldId';
import {
  SearchDocumentItem,
  SelectedFieldValue,
} from '~/features/advanced-search/types';
import { api } from '~/redux/api/createApi';
import { DocumentSearchResultItem } from '~/redux/api/methods/search';
import { MORE_FILTERS_SECTION_ID } from '~/redux/api/transformers/search';
import { SearchFilter } from '~/types/DashboardV2';
import { DocumentGroup } from '~/types/DocumentGroup';
import { FolderValue, renderDashboardsTableCellValue } from '~/utils/table';

type Props = {
  columnWidths: {
    [key: string]: number;
  };
  // TODO: change to EDS type once is available.
  hardcodedColumns: Array<any>;
  searchFilterColumns: SearchFilter[];
};

export const useSearchColumns = ({
  columnWidths,
  hardcodedColumns,
  searchFilterColumns,
}: Props) => {
  const { data: documentGroupsData } =
    api.endpoints.getAllDocumentGroups.useQuery(undefined);

  const renderGroupCell = useCallback(
    (document: SearchDocumentItem) => {
      if (document.selected_field_values?.document_group_id) {
        const { value } = document.selected_field_values?.document_group_id;
        return (
          <Link pathname={`/document-group/${value[0]}`}>
            {getGroupName(Number(value[0]), documentGroupsData?.results)}
          </Link>
        );
      } else {
        return null;
      }
    },
    [documentGroupsData?.results],
  );

  const columns = useMemo(
    () => [
      ...hardcodedColumns.map((column) => ({
        ...column,
        rawWidth: columnWidths[column.key],
      })),
      ...searchFilterColumns.map((searchFilter) => {
        return {
          ...fieldToColumn(searchFilter, renderGroupCell),
          rawWidth: columnWidths[searchFilter.id],
        };
      }),
    ],
    [searchFilterColumns, columnWidths, documentGroupsData?.results],
  );
  return columns;
};

const fieldToColumn = (
  searchFilter: SearchFilter,
  renderGroupCell: (document: SearchDocumentItem) => JSX.Element | null,
) => {
  if (searchFilter.id === 'document_group_id') {
    return {
      key: SearchableFields.DocumentGroup,
      title: searchFilter.label,
      minWidth: 'm',
      disableSortBy: true,
      renderCell: renderGroupCell,
      section: MORE_FILTERS_SECTION_ID,
    };
  } else if (searchFilter.type === 'folder') {
    return {
      key: 'folder',
      title: searchFilter.label,
      minWidth: 'm',
      disableSortBy: true,
      renderCell: (document: DocumentSearchResultItem) => (
        <FolderValue handlers={document.document_handlers as any} />
      ),
      section: MORE_FILTERS_SECTION_ID,
    };
  }
  const unsortableTypes = ['text'];

  return {
    key: String(searchFilter.id),
    field: searchFilter,
    title: searchFilter.label,
    disableSortBy: unsortableTypes.includes(searchFilter.type),
    cellType: getCellType(searchFilter.type),
    mapCellProps: (document: DocumentSearchResultItem) => {
      const text = renderDashboardsTableCellValue(
        searchFilter,
        document.selected_field_values,
      );
      const fieldValues = document.selected_field_values as
        | Record<FieldId, SelectedFieldValue>
        | undefined;
      const fieldValue = fieldValues?.[String(searchFilter.id)];
      return {
        text,
        datetime: isDate(text) ? parseDate(text) : undefined,
        number:
          searchFilter.type === 'number' && fieldValue?.value?.[0] != null
            ? fieldValue.value.map((item) => Number(item))
            : undefined,
      };
    },
    section: searchFilter.section,
  };
};

export const getGroupName = (
  groupId: number,
  documentGroups: DocumentGroup[] = [],
) => {
  return documentGroups.find((group) => group.groupId === groupId)?.name;
};

export const isDate = (value: string) => {
  return !isNaN(Date.parse(value));
};

export const getCellType = (value: string) => {
  switch (value) {
    case 'date':
      return 'datetime';
    case 'number':
      return 'number';

    default:
      return 'text';
  }
};
